import { useState, useEffect, useMemo } from 'react';
import EntityLink from '../components/EntityLink';
import { useNavigate } from 'react-router-dom';
import api, { downloadCsv, exportIdsParam } from '../utils/api';
import ClearFiltersButton, { EMPTY_PERIOD } from '../components/common/ClearFiltersButton';
import { useAuth } from '../context/AuthContext';
import { useUrlParams } from '../hooks/useUrlState';
import Pagination from '../components/Pagination';
import Badge from '../components/Badge';
import ActionsDropdown from '../components/ActionsDropdown';
import StateBlock from '../components/StateBlock';
import CsvImportModal from '../components/CsvImportModal';
import WorkOrderEditDrawer from '../components/WorkOrderEditDrawer';
import PurchaseOrderCreateDrawer from '../components/PurchaseOrderCreateDrawer';
import GenerateFteInvoiceModal from '../components/GenerateFteInvoiceModal';
import ConsultingInvoiceModal from '../components/ConsultingInvoiceModal';
import SortTh, { useSort } from '../components/common/SortableHeader';
import PeriodFilter, { usePeriodFilter } from '../components/PeriodFilter';
import { useConfirm, useToast } from '../context/FeedbackContext';

const ENGAGEMENT_LABELS = {
  contract_placement: 'Contract Placement',
  recurring_referral: 'Recurring Referral',
  fte_placement: 'FTE Placement',
  business_consulting: 'Business Consulting',
};

export default function WorkOrdersPage() {
  const confirm = useConfirm();
  const toast = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [workOrders, setWorkOrders] = useState(null);
  const [error, setError] = useState(false);
  const { get, set } = useUrlParams();
  const search = get('search', '');
  const typeFilter = get('type', 'all');
  const statusFilter = get('status', 'all');
  const page = get('page', 1);
  const pageSize = get('pageSize', 25);

  const [editTarget, setEditTarget] = useState(null);
  const [poTarget, setPoTarget] = useState(null);
  const [fteInvoiceTarget, setFteInvoiceTarget] = useState(null);
  const [consultingTarget, setConsultingTarget] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const { sortKey, sortDir, handleSort, sortData } = useSort('client_wo_number', 'asc');
  const { period, setPeriod, filterByPeriod } = usePeriodFilter("start_date");

  function load() {
    setError(false);
    api.get('/work-orders')
      .then(r => setWorkOrders(r.data))
      .catch(() => setError(true));
  }

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!workOrders) return [];
    const statusTypeSearchFiltered = workOrders.filter(wo => {
      if (search && !wo.client_wo_number.toLowerCase().includes(search.toLowerCase()) &&
        !(wo.contractor_name || '').toLowerCase().includes(search.toLowerCase()) &&
        !(wo.client_name || '').toLowerCase().includes(search.toLowerCase())) return false;
      if (typeFilter !== 'all' && wo.engagement_type !== typeFilter) return false;
      if (statusFilter !== 'all' && wo.status !== statusFilter) return false;
      return true;
    });
    return filterByPeriod(statusTypeSearchFiltered);
  }, [workOrders, search, typeFilter, statusFilter, period, filterByPeriod]);

  const sorted = useMemo(() => {
    return sortData(filtered, {
      client_wo_number: (w) => w.client_wo_number || "",
      outgoing_wo_number: (w) => w.outgoing_wo_number || "",
      engagement_type: (w) => w.engagement_type || "",
      client_name: (w) => w.client_name || "",
      vendor_name: (w) => w.vendor_name || "",
      contractor_name: (w) => w.contractor_name || "",
      status: (w) => w.status || "",
    });
  }, [filtered, sortKey, sortDir]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, page, pageSize]);

  function handleFilterChange(key, value) {
    set({ [key]: value, page: 1 });
  }

  async function handleSuspend(wo) {
    if (!(await confirm({ title: `Suspend WO ${wo.client_wo_number}?`, message: 'This voids the Work Order and clears its start/end dates.', confirmLabel: 'Suspend', danger: true }))) return;
    try {
      await api.put(`/work-orders/${wo.id}`, {
        status: 'suspended',
        start_date: null,
        end_date: null,
        notes: `${wo.notes ? wo.notes + ' | ' : ''}Voided — consultant never had billable work`,
      });
      load();
    } catch (e) {
      toast('Failed to suspend: ' + (e.response?.data?.error || e.message), 'error');
    }
  }

  async function handleExport() {
    await downloadCsv('/work-orders/export', 'work-orders-export.csv', exportIdsParam(filtered, workOrders));
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">
            Work Orders {workOrders && <span className="page-title-count">({workOrders.length} total)</span>}
          </h1>
          <p className="page-subtitle">Contract Placements, Recurring Referrals, FTE Placements, and Business Consulting.</p>
        </div>
        <div className="page-actions">
          {user?.role === 'admin' && (
            <button className="btn btn-secondary" onClick={handleExport}>↓ Export CSV</button>
          )}
          <button className="btn btn-secondary" onClick={() => setShowImport(true)}>↑ Upload CSV</button>
          <button className="btn btn-primary" onClick={() => setEditTarget({})}>+ New Work Order</button>
        </div>
      </div>

      <div className="filter-bar">
        <PeriodFilter value={period} onChange={setPeriod} />
        <input
          type="text"
          className="filter-search"
          placeholder="Search WO #, contractor, or client…"
          value={search}
          onChange={e => handleFilterChange('search', e.target.value)}
        />
        <select value={typeFilter} onChange={e => handleFilterChange('type', e.target.value)}>
          <option value="all">All Types</option>
          <option value="contract_placement">Contract Placement</option>
          <option value="recurring_referral">Recurring Referral</option>
          <option value="fte_placement">FTE Placement</option>
          <option value="business_consulting">Business Consulting</option>
        </select>
        <select value={statusFilter} onChange={e => handleFilterChange('status', e.target.value)}>
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="closed">Closed</option>
          <option value="suspended">Suspended</option>
        </select>
        <ClearFiltersButton
          active={!!(search || typeFilter !== 'all' || statusFilter !== 'all' || period.from || period.to)}
          onClear={() => { set({ search: '', type: '', status: '', page: 1 }); setPeriod(EMPTY_PERIOD); }}
        />
      </div>

      {error && <StateBlock type="error" action={<button className="btn btn-primary" onClick={load}>Try again</button>} />}
      {!error && workOrders === null && <StateBlock type="loading" />}
      {!error && workOrders !== null && filtered.length === 0 && (
        <StateBlock type="noResults" message="No Work Orders match this search. Clear filters or create a new one." />
      )}

      {!error && filtered.length > 0 && (
        <>
          <table className="data-table">
            <thead>
              <tr>
                <SortTh label="Client WO #" sortKey="client_wo_number" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortTh label="Outgoing WO #" sortKey="outgoing_wo_number" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortTh label="Type" sortKey="engagement_type" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortTh label="Client" sortKey="client_name" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortTh label="Vendor" sortKey="vendor_name" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortTh label="Contractor" sortKey="contractor_name" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortTh label="Status" sortKey="status" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paged.map(wo => (
                <tr key={wo.id}>
                  <td><EntityLink type="work_order" id={wo.id} style={{ fontWeight: 600 }}>{wo.client_wo_number}</EntityLink></td>
                  <td><EntityLink type="outgoing_wo" id={wo.id}>{wo.outgoing_wo_number || '—'}</EntityLink></td>
                  <td><Badge variant="info">{ENGAGEMENT_LABELS[wo.engagement_type]}</Badge></td>
                  <td><EntityLink type="client" id={wo.client_id}>{wo.client_name || "—"}</EntityLink></td>
                  <td><EntityLink type="vendor" id={wo.vendor_id}>{wo.vendor_name || "—"}</EntityLink></td>
                  <td><EntityLink type="contractor" id={wo.contractor_id}>{wo.contractor_name || "—"}</EntityLink></td>
                  <td><Badge variant={wo.status === 'active' ? 'success' : 'neutral'}>{wo.status}</Badge></td>
                  <td style={{ textAlign: 'right' }}>
                    <ActionsDropdown actions={[
                      { label: 'Edit', onClick: () => setEditTarget(wo) },
                      ...(wo.status !== 'suspended'
                        ? [{ label: 'Suspend (Void)', onClick: () => handleSuspend(wo) }]
                        : []),
                      ...(wo.engagement_type === 'business_consulting'
                        ? [{ label: 'New consulting invoice', onClick: () => setConsultingTarget(wo) },
                           { label: 'Add client PO (optional)', onClick: () => setPoTarget(wo) }]
                        : wo.engagement_type !== 'fte_placement'
                        ? [{ label: 'Create PO', onClick: () => setPoTarget(wo) }]
                        : []),
                      ...(wo.engagement_type === 'fte_placement' && wo.fte_fee_amount && !wo.fte_fee_invoiced
                        ? [{ label: 'Generate Invoice', onClick: () => setFteInvoiceTarget(wo) }]
                        : []),
                    ]} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            page={page} pageSize={pageSize} total={filtered.length}
            onPageChange={(p) => set({ page: p })}
            onPageSizeChange={(s) => set({ pageSize: s, page: 1 })}
          />
        </>
      )}

      {editTarget !== null && (
        <WorkOrderEditDrawer
          workOrder={editTarget.id ? editTarget : null}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); load(); }}
        />
      )}

      {poTarget !== null && (
        <PurchaseOrderCreateDrawer
          workOrder={poTarget}
          onClose={() => setPoTarget(null)}
          onSaved={() => load()}
        />
      )}

      {fteInvoiceTarget !== null && (
        <GenerateFteInvoiceModal
          workOrder={fteInvoiceTarget}
          onClose={() => setFteInvoiceTarget(null)}
          onGenerated={() => load()}
        />
      )}

      {consultingTarget !== null && (
        <ConsultingInvoiceModal
          workOrder={consultingTarget}
          onClose={() => setConsultingTarget(null)}
          onSaved={(r) => { setConsultingTarget(null); toast(`Invoice ${r.invoice_number} created as Draft - open it from AR Invoices to submit or download the PDF`, 'success'); }}
        />
      )}

      {showImport && (
        <CsvImportModal
          entityLabel="Work Order"
          importPath="/work-orders/import"
          onClose={() => setShowImport(false)}
          onComplete={load}
        />
      )}
    </div>
  );
}
